import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, Shield, ArrowRight, Lock, Play, XCircle } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What is the bridge sheet?',
    options: [
      'A sheet that the client edits directly',
      'An internal sheet that imports and filters data for one client only',
      'A backup of the master sheet',
      'A sheet that combines clicks and conversions',
    ],
    correctIndex: 1,
    explanation:
      'The bridge sheet is an internal sheet that uses IMPORTRANGE + FILTER to pull only one client\'s data from the master. It acts as a firewall between master data and the client.',
  },
  {
    question: 'How many IMPORTRANGE formulas are in this pattern total?',
    options: [
      '1 — in the client sheet from master',
      '2 — one in the bridge (from master) and one in the client sheet (from bridge)',
      '3 — one in each sheet',
      '0 — it uses VLOOKUP instead',
    ],
    correctIndex: 1,
    explanation:
      'There are exactly 2 IMPORTRANGE formulas: the bridge imports from the master (with a FILTER), and the client sheet imports from the bridge.',
  },
  {
    question: 'Why can\'t the client access other offers\' data through the client sheet?',
    options: [
      'The formulas are password-protected',
      'Google Sheets blocks it automatically',
      'The client sheet only links to the bridge, which only contains their offer\'s data',
      'The client does not have edit access',
    ],
    correctIndex: 2,
    explanation:
      'The client sheet\'s IMPORTRANGE points at the bridge, not the master. The bridge only contains data for one offer. Even if the client edits their IMPORTRANGE, the bridge has nothing else to see.',
  },
  {
    question: 'Who should have access to the bridge sheet?',
    options: [
      'The client and your team',
      'Only your internal team — never the client',
      'Only the client',
      'It should be publicly accessible',
    ],
    correctIndex: 1,
    explanation:
      'The bridge sheet is internal only. The client should only ever see their client sheet. Sharing the bridge would undermine the security pattern.',
  },
  {
    question: 'What formula does the bridge sheet use?',
    options: [
      'VLOOKUP from the master sheet',
      'QUERY from the master sheet',
      'FILTER combined with IMPORTRANGE to pull only one offer\'s data from the master',
      'INDEX/MATCH from the master sheet',
    ],
    correctIndex: 2,
    explanation:
      'The bridge uses FILTER(IMPORTRANGE(...), condition) to import data from the master AND filter it to only one offer\'s rows, all in a single formula.',
  },
  {
    question: 'What does the client sheet\'s IMPORTRANGE point at?',
    options: [
      'The master sheet directly',
      'The bridge sheet',
      'A Google Drive folder',
      'The clicks sheet',
    ],
    correctIndex: 1,
    explanation:
      'The client sheet\'s IMPORTRANGE points at the bridge sheet, not the master. This is the key security layer — the client never has any connection to the master data.',
  },
  {
    question: 'What happens if the client edits the IMPORTRANGE URL in their client sheet?',
    options: [
      'They can see all master data',
      'They need to know the master URL, which they don\'t have — so it won\'t work',
      'Google automatically redirects them to the bridge',
      'The formula deletes itself',
    ],
    correctIndex: 1,
    explanation:
      'The client doesn\'t know the master sheet\'s URL. Even if they change the IMPORTRANGE URL, they would need the exact URL of the master AND it would need to have granted access — neither of which they have.',
  },
  {
    question: 'How many Google Sheets are involved in the full bridge pattern?',
    options: [
      '1 — everything is in one sheet',
      '2 — master and client',
      '3 — master, bridge, and client',
      '4 — clicks, conversions, bridge, and client',
    ],
    correctIndex: 2,
    explanation:
      'The bridge pattern uses three sheets: the master (all data), the bridge (filtered to one offer), and the client sheet (imports from the bridge). Each is a separate Google Sheets file.',
  },
  {
    question: 'If you need to set up reports for 5 different clients, how many bridge sheets do you need?',
    options: [
      '1 bridge sheet shared by all clients',
      '5 — one bridge sheet per client',
      '0 — bridge sheets are optional with multiple clients',
      '10 — two per client',
    ],
    correctIndex: 1,
    explanation:
      'Each client needs their own bridge sheet that filters the master data to only their offer(s). Sharing a single bridge between clients would defeat the purpose of data isolation.',
  },
  {
    question: 'What is the FILTER condition in the bridge sheet formula typically based on?',
    options: [
      'The date column',
      'The offer ID matching the client\'s offer',
      'The number of clicks being above zero',
      'The row number',
    ],
    correctIndex: 1,
    explanation:
      'The FILTER condition checks the Offer ID column against the client\'s specific offer ID. This ensures only rows belonging to that client\'s offer are pulled into the bridge.',
  },
  {
    question: 'Which sheet in the bridge pattern should you share with the client?',
    options: [
      'The master sheet with view-only access',
      'The bridge sheet with view-only access',
      'Only the client sheet',
      'Both the bridge and client sheets',
    ],
    correctIndex: 2,
    explanation:
      'Only the client sheet should ever be shared with the client. The master and bridge sheets are internal. Sharing either of those would compromise the security of the pattern.',
  },
  {
    question: 'What is the correct order of steps when setting up a new client report?',
    options: [
      'Create client sheet, then bridge, then share master',
      'Create bridge with FILTER + IMPORTRANGE from master, create client sheet with IMPORTRANGE from bridge, share only the client sheet',
      'Share the master with the client, then create a bridge',
      'Create the client sheet first, then IMPORTRANGE directly from master',
    ],
    correctIndex: 1,
    explanation:
      'The correct workflow is: (1) create the bridge sheet with FILTER + IMPORTRANGE from the master, (2) create the client sheet with IMPORTRANGE from the bridge, (3) share only the client sheet with the client.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Architecture Flow Diagram ─── */
function ArchitectureFlow() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const STEPS = [
    { label: 'Master Sheet', description: 'Your internal sheet with ALL offers\' combined data.', active: [0] },
    { label: 'IMPORTRANGE + FILTER', description: 'The bridge imports from master but only keeps offer_123 rows.', active: [0, 1] },
    { label: 'Bridge Sheet', description: 'Now contains ONLY offer_123 data. This is the firewall.', active: [1] },
    { label: 'IMPORTRANGE', description: 'The client sheet imports everything from the bridge.', active: [1, 2] },
    { label: 'Client Sheet', description: 'The client sees their data. They cannot reach the master.', active: [2] },
  ]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= STEPS.length - 1) {
      setIsPlaying(false)
      return
    }
    const timer = setTimeout(() => setStep((s) => s + 1), 2000)
    return () => clearTimeout(timer)
  }, [step, isPlaying])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const current = STEPS[step]

  const boxes = [
    { label: 'Master Sheet', sublabel: 'Internal, all data', color: 'blue', icon: '📊' },
    { label: 'Bridge Sheet', sublabel: 'Internal, one offer', color: 'amber', icon: '🛡️' },
    { label: 'Client Sheet', sublabel: 'Shared with client', color: 'emerald', icon: '📄' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          <Play className="w-4 h-4" />
          {step > 0 ? 'Replay' : 'Play Flow'}
        </button>
        <span className="text-xs text-gray-400">Step {step + 1} of {STEPS.length}</span>
      </div>

      <div className="flex items-center justify-between gap-2 py-4">
        {boxes.map((box, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <motion.div
              animate={{
                scale: current.active.includes(i) ? 1.05 : 0.95,
                opacity: current.active.includes(i) ? 1 : 0.4,
              }}
              transition={{ duration: 0.3 }}
              className={`flex-1 rounded-xl border-2 p-4 text-center border-${box.color}-300 bg-${box.color}-50`}
            >
              <div className="text-2xl mb-1">{box.icon}</div>
              <p className={`text-sm font-bold text-${box.color}-800`}>{box.label}</p>
              <p className={`text-xs text-${box.color}-600`}>{box.sublabel}</p>
            </motion.div>
            {i < boxes.length - 1 && (
              <motion.div
                animate={{ opacity: (current.active.includes(i) && current.active.includes(i + 1)) ? 1 : 0.2 }}
              >
                <ArrowRight className="w-5 h-5 text-gray-400 shrink-0" />
              </motion.div>
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3"
        >
          <p className="text-sm font-semibold text-red-800">{current.label}</p>
          <p className="text-sm text-red-700">{current.description}</p>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module12() {
  return (
    <ModuleLayout moduleId={14}>
      <div className="space-y-8">
        {/* Section 1: The Architecture */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Architecture</h2>
              <p className="text-lg text-gray-600">
                Three layers. The bridge sheet is the firewall. It only contains one offer's data.
              </p>
            </div>

            <ArchitectureFlow />
          </div>
        </Section>

        {/* Section 2: Setting Up the Bridge */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Setting Up the Bridge</h2>
              <p className="text-lg text-gray-600">
                One formula does all the work: import from master AND filter to one offer.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
              <code className="text-sm text-amber-400 font-mono whitespace-nowrap">
                =FILTER(IMPORTRANGE("master_url", "Combined!A:E"), IMPORTRANGE("master_url", "Combined!B:B")="offer_123")
              </code>
            </div>

            <div className="space-y-3">
              {[
                { num: 1, text: 'IMPORTRANGE pulls ALL data from the master sheet', color: 'blue' },
                { num: 2, text: 'FILTER keeps only rows where Offer ID = "offer_123"', color: 'amber' },
                { num: 3, text: 'The bridge sheet now contains ONLY offer_123 data', color: 'emerald' },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`flex items-start gap-3 rounded-lg border border-${step.color}-200 bg-${step.color}-50 p-4`}
                >
                  <span className={`flex items-center justify-center w-7 h-7 rounded-full bg-${step.color}-200 text-${step.color}-800 font-bold text-sm shrink-0`}>
                    {step.num}
                  </span>
                  <p className={`text-sm text-${step.color}-800`}>{step.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 3: Setting Up the Client Sheet */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Setting Up the Client Sheet</h2>
              <p className="text-lg text-gray-600">
                The client sheet simply imports everything from the bridge.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-sm text-emerald-400 font-mono">
                =IMPORTRANGE("bridge_url", "Sheet1!A:E")
              </code>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 space-y-2">
              <p className="text-emerald-800 font-semibold">The client sheet only sees the bridge.</p>
              <p className="text-sm text-emerald-700">The bridge only has their data.</p>
              <p className="text-sm text-emerald-700">Even if the client edits this formula, they can only access the bridge — which has nothing but their offer.</p>
            </div>
          </div>
        </Section>

        {/* Section 4: Why This is Safe */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Why This is Safe</h2>
              <p className="text-lg text-gray-600">
                What happens if the client tries to be sneaky?
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  attack: 'They edit the IMPORTRANGE URL?',
                  defense: "They would need to know the master sheet URL (they don't have it).",
                },
                {
                  attack: 'They edit the range?',
                  defense: 'The bridge only has their data anyway — there is nothing else to see.',
                },
                {
                  attack: 'They remove IMPORTRANGE and try a different one?',
                  defense: "They don't have access to the master sheet. Google will block it.",
                },
              ].map((scenario, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5"
                >
                  <div className="shrink-0">
                    <XCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{scenario.attack}</p>
                    <p className="text-sm text-gray-600 mt-1">{scenario.defense}</p>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 5: The Workflow Checklist */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Workflow Checklist</h2>
              <p className="text-lg text-gray-600">
                Follow these steps every time you set up a new client report.
              </p>
            </div>

            <div className="space-y-3">
              {[
                { step: 'Build your combined dataset', ref: 'Module 9' },
                { step: 'Create a bridge sheet for the client', ref: 'New Google Sheet' },
                { step: "Add the FILTER + IMPORTRANGE formula with the client's offer IDs", ref: 'Bridge sheet' },
                { step: 'Create the client sheet', ref: 'New Google Sheet' },
                { step: 'Add IMPORTRANGE pointing at the bridge', ref: 'Client sheet' },
                { step: 'Share ONLY the client sheet with the client', ref: 'Google sharing' },
                { step: 'NEVER share the master or bridge sheets', ref: 'Security rule' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-bold text-sm shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${i === 6 ? 'text-red-700' : 'text-gray-800'}`}>{item.step}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.ref}</p>
                  </div>
                  {i === 6 && <Lock className="w-4 h-4 text-red-500 shrink-0" />}
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 6: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={14} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
